/* APP - Added Quick Lists Route */
import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/Layout/Sidebar';
import { Topbar } from './components/Layout/Topbar';
import { Dashboard } from './pages/Dashboard';
import { GanttView } from './pages/GanttView';
import { CalendarView } from './pages/CalendarView';
import { ProjectBuilder } from './pages/ProjectBuilder';
import { BusinessUnits } from './pages/BusinessUnits';
import { TasksPage } from './pages/Library/TasksPage';
import { WorkflowsPage } from './pages/Library/WorkflowsPage';
import { ProjectsPage } from './pages/Library/ProjectsPage';
import { UsersPage } from './pages/UsersPage';
import { ProfilePage } from './pages/ProfilePage';
import { QuickLists } from './pages/QuickLists';
import { Login } from './pages/Login';
import { StoreProvider, useStore } from './store/StoreContext';

const AuthLinkHandler = ({ children }) => {
  const { state } = useStore();
  const hash = window.location.hash;
  const isAuthFlow = hash.includes('access_token=') || hash.includes('type=recovery') || hash.includes('error_description=');
  
  if (state.loading && !state.currentUser && !isAuthFlow) {
    return (
      <div className="h-screen w-screen bg-base-900 flex flex-col items-center justify-center gap-6">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        <div className="text-center">
          <p className="text-gray-500 font-mono text-[10px] uppercase tracking-widest animate-pulse">Connecting to Nest...</p>
        </div>
      </div>
    );
  }
  
  if (state.isRecoveryMode || isAuthFlow) return <Login />;
  if (!state.currentUser) return <Login />;
  return children;
};

const ProtectedLayout = ({ children }) => {
  const { state } = useStore();
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-base-900 text-white font-sans selection:bg-primary/30">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 bg-base-900">
        <Topbar />
        <main className="flex-1 overflow-hidden relative">
          {children}
          {state.loading && (
            <div className="absolute top-4 right-4 z-50">
              <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const AdminRoute = ({ children }) => {
  const { state } = useStore();
  const role = state.currentUser?.role;
  const canManageUsers = ['master_admin', 'admin', 'admin_enterprise', 'admin_gold'].includes(role);
  if (!canManageUsers) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <StoreProvider>
      <HashRouter>
        <AuthLinkHandler>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<ProtectedLayout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/quick-lists" element={<QuickLists />} />
                <Route path="/gantt" element={<GanttView />} />
                <Route path="/calendar" element={<CalendarView />} />
                <Route path="/builder" element={<ProjectBuilder />} />
                <Route path="/builder/:projectId" element={<ProjectBuilder />} />
                <Route path="/library/tasks" element={<TasksPage />} />
                <Route path="/library/workflows" element={<WorkflowsPage />} />
                <Route path="/library/projects" element={<ProjectsPage />} />
                <Route path="/business-units" element={<BusinessUnits />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ProtectedLayout>} />
          </Routes>
        </AuthLinkHandler>
      </HashRouter>
    </StoreProvider>
  );
}

export default App;